import { KeyboardReturnRounded } from '@mui/icons-material';
import {
  alpha,
  Box,
  Checkbox,
  ListItemIcon,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import Highlighter from 'react-highlight-words';
import { UUID } from 'v2/core/entities/uuid';

const ListItem = styled('li')(() => ({
  '&.Mui-focused': {
    '.KeyboardIconContainer': {
      opacity: 1,
    },
    '.EditIconContainer': {
      opacity: 1,
    },
  },
  '.Truncate': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface AutoCompleteOptionProps {
  baseProps: React.HTMLAttributes<HTMLLIElement>;
  option: { id: UUID; displayName: string; creatorName?: string };
  renderLabelAvatar?: (
    option: AutoCompleteOptionProps['option'],
  ) => JSX.Element;
  renderSecondaryAction?: (
    option: AutoCompleteOptionProps['option'],
  ) => ReactNode;
  searchTerm: string;
  showCheckbox?: boolean;
  selected?: boolean;
  style?: React.CSSProperties;
  shouldTruncate?: boolean;
  renderTooltipTitle?: () => ReactNode;
}

export function AutoCompleteOption({
  baseProps,
  option,
  searchTerm,
  renderLabelAvatar,
  renderSecondaryAction,
  showCheckbox = false,
  selected,
  shouldTruncate = false,
  style,
  renderTooltipTitle: renderTooltipTitleProp,
}: AutoCompleteOptionProps) {
  const renderTooltipTitle = () => {
    if (renderTooltipTitleProp) {
      return renderTooltipTitleProp();
    }

    if (shouldTruncate) {
      return option.displayName ?? option.id;
    }
    return null;
  };

  return (
    <ListItem
      {...baseProps}
      key={option.id}
      sx={{ width: '100%' }}
      style={style}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ width: '100%' }}
        alignItems="center"
      >
        <Tooltip title={renderTooltipTitle()} arrow placement="right">
          <Stack direction="row" alignItems="center" overflow="hidden">
            {showCheckbox && (
              <ListItemIcon sx={{ minWidth: 'unset' }}>
                <Checkbox
                  edge="start"
                  tabIndex={-1}
                  disableRipple
                  checked={selected}
                  size="small"
                />
              </ListItemIcon>
            )}
            {renderLabelAvatar ? <Box>{renderLabelAvatar(option)}</Box> : null}
            <Highlighter
              textToHighlight={option.displayName ?? option?.id ?? ''}
              searchWords={[searchTerm]}
              autoEscape={true}
              highlightTag={({ children }) => (
                <strong className="highlighted-text">{children}</strong>
              )}
              className={shouldTruncate ? 'Truncate' : undefined}
            />
            {option.creatorName ? (
              <Typography variant="caption" color="text.disabled" pl={1}>
                {option.creatorName}
              </Typography>
            ) : null}
          </Stack>
        </Tooltip>
        <Stack direction="row" alignItems="center" spacing={1} pl={1}>
          {renderSecondaryAction ? renderSecondaryAction(option) : null}
          <Box
            component="span"
            bgcolor={(theme) => alpha(theme.palette.primary.main, 0.16)}
            className="KeyboardIconContainer"
            px={1}
            borderRadius={1}
            sx={{ opacity: 0 }}
          >
            <KeyboardReturnRounded
              fontSize="small"
              sx={{ color: (theme) => theme.palette.primary.main }}
            />
          </Box>
        </Stack>
      </Stack>
    </ListItem>
  );
}
